/**
 * Felix Quiz (Light DOM)
 *
 * Manages multi-step quiz interaction. Shows the first step on load,
 * advances sequentially when an answer is clicked, and reports the funnel:
 * which step was reached, and the completion that leaves for onboarding.
 *
 * @package Felix
 * @since 1.0.0
 */

class FelixQuiz extends HTMLElement {
	connectedCallback() {
		if (this._isConnected) {
			return
		}

		this._isConnected = true

		this.steps = Array.from(this.querySelectorAll('[data-step]'))
		if (!this.steps.length) {
			return
		}

		this.dots = Array.from(this.querySelectorAll('.quiz__progress-dot'))
		this.counter = this.querySelector('[data-quiz-current]')
		this.modalHost = this.closest('felix-modal')
		this.controlsRoot = this.modalHost || this
		this.backButton = this.controlsRoot.querySelector('[data-quiz-back]')

		// Hide all steps except the first.
		this.steps.forEach((step, i) => {
			if (i !== 0) {
				step.hidden = true
			}
		})
		this.currentIndex = 0
		this.animateStep(this.steps[this.currentIndex])
		this.updateControls()

		this.onAnswer = this.onAnswer.bind(this)
		this.controlsRoot.addEventListener('click', this.onAnswer)

		// A quiz inside a modal is not on screen until the modal opens, so that is
		// where its funnel starts. The already-open check covers the modal opening
		// itself before this element upgrades, which data-open-by-default can do.
		if (!this.modalHost) {
			this.trackStart()
			return
		}

		this.onModalOpen = this.onModalOpen.bind(this)
		this.modalHost.addEventListener('felix-modal:open', this.onModalOpen)
		if (this.modalHost.querySelector('dialog')?.open) {
			this.trackStart()
		}
	}

	disconnectedCallback() {
		if (this.controlsRoot) {
			this.controlsRoot.removeEventListener('click', this.onAnswer)
		}
		if (this.modalHost) {
			this.modalHost.removeEventListener('felix-modal:open', this.onModalOpen)
		}
	}

	onModalOpen() {
		this.trackStart()
	}

	/**
	 * Linked answers leave the site, and GTM works out which tags to run after the
	 * push returns. Reporting and letting the click through therefore sends
	 * nothing: the page is gone before any tag runs. So the completion holds the
	 * navigation until the tags report, which is usually a few tens of
	 * milliseconds and is capped inside felixTrack.
	 *
	 * A click the browser handles its own way — new tab, new window, download —
	 * leaves this page alive, so it reports and gets out of the way.
	 */
	completeVia(e, btn) {
		const destination = btn.getAttribute('href') || ''
		const report = (onSent) => window.felixTrack?.('quiz_completed', { ...this.trackingContext(), destination }, onSent)

		const browserHandlesIt = e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || btn.target === '_blank'

		if (browserHandlesIt || !destination || !window.felixTrack) {
			report()
			return
		}

		e.preventDefault()
		this.isCompleting = true
		report(() => {
			window.location.href = destination
		})
	}

	/**
	 * felixTrack lives in the global bundle, which loads with defer and therefore
	 * runs after this blocking script — and after DOMContentLoaded is scheduled
	 * but before it fires. Reporting a visible first step any earlier pushes into
	 * an API that does not exist yet, and the optional call swallows it in silence.
	 * Anything triggered by a click is already past this and needs no wait.
	 */
	whenTrackingReady(fn) {
		if (document.readyState === 'loading') {
			document.addEventListener('DOMContentLoaded', fn, { once: true })
			return
		}
		fn()
	}

	/**
	 * Reports which step someone reached. The step is read now and sent later,
	 * so a question answered before the page finished loading is still reported
	 * as the question it was, rather than as whichever one is showing by the time
	 * the tracking code arrives.
	 */
	report(event, context) {
		const payload = context || this.trackingContext()

		this.whenTrackingReady(() => window.felixTrack?.(event, payload))
	}

	/**
	 * Context every quiz event carries. stepIndex is 1-based so it reads the same
	 * way the progress counter does; pageName is added by felixTrack itself.
	 */
	trackingContext() {
		const step = this.steps[this.currentIndex]
		const heading = step?.querySelector('.quiz-step__heading')

		return {
			stepIndex: this.currentIndex + 1,
			stepTotal: this.steps.length,
			stepQuestion: heading ? heading.textContent.replace(/\s+/g, ' ').trim() : '',
		}
	}

	/**
	 * Once per element: reopening the modal resumes the same funnel rather than
	 * starting a new one. Step one reports twice on purpose — quiz_started is the
	 * denominator, and quiz_step_viewed stays uniform so the furthest step reached
	 * is a single max() downstream.
	 */
	trackStart() {
		if (this.hasStarted) {
			return
		}
		this.hasStarted = true

		const context = this.trackingContext()
		this.report('quiz_started', context)
		this.report('quiz_step_viewed', context)
	}

	onAnswer(e) {
		// The completion holds the click, so the answers stay live and clickable.
		// Without this a second click reports a second completion.
		if (this.isCompleting) {
			e.preventDefault()
			return
		}

		const back = e.target.closest('[data-quiz-back]')
		if (back) {
			e.preventDefault()
			this.goToStep(this.currentIndex - 1)
			return
		}

		const btn = e.target.closest('[data-answer]')
		if (!btn) {
			return
		}

		if (btn.hasAttribute('data-answer-link')) {
			this.completeVia(e, btn)
			return
		}

		e.preventDefault()
		this.goToStep(this.currentIndex + 1)
	}

	goToStep(index) {
		if (index < 0) {
			return
		}

		if (index >= this.steps.length) {
			this.dispatchEvent(new CustomEvent('quiz:complete', { bubbles: true }))
			return
		}

		this.steps[this.currentIndex].hidden = true
		this.currentIndex = index
		this.steps[this.currentIndex].hidden = false
		this.animateStep(this.steps[this.currentIndex])

		this.dots.forEach((dot, i) => {
			if (i === index) {
				dot.setAttribute('data-active', '')
			} else {
				dot.removeAttribute('data-active')
			}
		})

		if (this.counter) {
			this.counter.textContent = String(index + 1)
		}

		this.updateControls()

		this.report('quiz_step_viewed')
	}

	animateStep(step) {
		if (!step) {
			return
		}

		const animatables = Array.from(step.querySelectorAll('[data-animate]'))
		if (!animatables.length) {
			return
		}

		// Re-trigger standard animation utilities whenever a step becomes active.
		animatables.forEach((el) => {
			el.classList.remove('is-animated')
		})

		requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				animatables.forEach((el) => {
					el.classList.add('is-animated')
				})
			})
		})
	}

	updateControls() {
		if (!this.backButton) {
			return
		}

		this.backButton.hidden = this.currentIndex === 0
	}
}

customElements.define('felix-quiz', FelixQuiz)
